import React from "react";
import ReactDOM from "react-dom";
import { Capacitor } from "@capacitor/core";
import config from "./config";
import { appendGtagScriptToHead } from "./lib/googleAnalytics";
import loadConfig from "./loadConfig";
import PageError from "./pages/PageError";
import * as serviceWorker from "./serviceWorker";
import { bootstrapDesktop } from "./desktop";
import { pruneStaleEntries } from "./lib/offlineCache";
import { installConsoleTaps } from "./lib/diagnosticDump";

// Hook console.warn / console.error into the diagnostic ring buffer so
// the "copy diagnostic" button in the conference UI captures issues
// logged anywhere in the app — third-party libs included.
installConsoleTaps();

function createApp() {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const App = require("./App").default;
  ReactDOM.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
    document.getElementById("root")
  );
}

function setTitle() {
  const titleTag = document.createElement("TITLE");
  const siteName = document.createTextNode(config.siteName);
  titleTag.appendChild(siteName);
  document.head.appendChild(titleTag);
}

loadConfig()
  .then(() => appendGtagScriptToHead())
  .then(() => setTitle())
  .then(() =>
    bootstrapDesktop({
      onDeepLinkNavigate: (path) => {
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      },
      onNotificationAction: (chatId) => {
        window.location.href = `/messages/${chatId}`;
      },
      onMenuNavigate: (path) => {
        window.history.pushState({}, "", path);
        window.dispatchEvent(new PopStateEvent("popstate"));
      },
    })
  )
  .then(() => {
    createApp();
    pruneStaleEntries().catch(() => {});
  })
  .catch((e) => {
    ReactDOM.render(
      <React.StrictMode>
        <PageError error={e} />
      </React.StrictMode>,
      document.getElementById("root")
    );
  });

// Minimal, framework-free "new version available" banner. A precaching service
// worker won't serve a new build until it takes control, so we let the user opt
// in to an immediate refresh instead of being stuck on a stale bundle.
function showUpdateBanner(onReload: () => void) {
  if (document.getElementById("sw-update-banner")) return;
  const bar = document.createElement("div");
  bar.id = "sw-update-banner";
  bar.style.cssText =
    "position:fixed;left:50%;bottom:24px;transform:translateX(-50%);z-index:99999;" +
    "background:#222;color:#fff;padding:10px 16px;border-radius:8px;display:flex;gap:12px;" +
    "align-items:center;box-shadow:0 4px 16px rgba(0,0,0,.3);font:14px/1.2 sans-serif;";
  const text = document.createElement("span");
  text.textContent = "A new version is available.";
  const btn = document.createElement("button");
  btn.textContent = "Reload";
  btn.style.cssText =
    "background:#4f8cff;color:#fff;border:0;border-radius:6px;padding:6px 12px;cursor:pointer;font:inherit;";
  btn.onclick = () => {
    btn.disabled = true;
    onReload();
  };
  bar.appendChild(text);
  bar.appendChild(btn);
  document.body.appendChild(bar);
}

// Register the unified service worker (offline app shell + web push) in
// production browsers only. Skip the desktop (Electron) build and Capacitor
// native apps: they load a packaged bundle (already offline-capable), and a
// precaching SW in the Android WebView could serve a stale bundle after a
// native app update.
if (process.env.REACT_APP_DESKTOP === "true" || Capacitor.isNativePlatform()) {
  serviceWorker.unregister();
} else {
  serviceWorker.register({
    onUpdate: (registration) => {
      const waiting = registration.waiting;
      if (!waiting) return;
      let reloaded = false;
      const onControllerChange = () => {
        if (reloaded) return;
        reloaded = true;
        // Detach before reloading so repeated onUpdate calls in one session
        // can't stack listeners and multiply reloads.
        navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
        window.location.reload();
      };
      navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
      showUpdateBanner(() => waiting.postMessage({ type: "SKIP_WAITING" }));
    },
  });
}
